import React from 'react';
import { AlertTriangle, XCircle, Clock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Subscription Warning Banner
 * Shows warning when subscription is expiring, in grace period, or expired
 */
export default function SubscriptionWarningBanner({ subscriptionStatus, onRenew, onDismiss }) {
  if (!subscriptionStatus) return null;
  
  const { status, days_remaining, is_grace_period, is_expired } = subscriptionStatus;
  
  // Don't show for active subscriptions with more than 7 days
  if (status === 'active' && (days_remaining === null || days_remaining > 7)) {
    return null;
  }
  
  // Expiring soon (7 days or less)
  if (status === 'active' && days_remaining !== null && days_remaining <= 7) {
    return (
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3" data-testid="subscription-warning-banner">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-yellow-600" />
            <div>
              <span className="text-yellow-800 font-medium">Subscription expiring soon!</span>
              <span className="text-yellow-700 ml-2">
                {days_remaining} day{days_remaining !== 1 ? 's' : ''} remaining
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
              onClick={onRenew}
            >
              Renew Now
            </Button>
            {onDismiss && (
              <button onClick={onDismiss} className="text-yellow-600 hover:text-yellow-800 p-1">
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Grace Period
  if (is_grace_period || status === 'grace_period') {
    return (
      <div className="bg-orange-50 border-b border-orange-300 px-4 py-3" data-testid="subscription-grace-banner">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div>
              <span className="text-orange-800 font-medium">Grace Period Active!</span>
              <span className="text-orange-700 ml-2">
                Your subscription has expired. {days_remaining} day{days_remaining !== 1 ? 's' : ''} left to renew before read-only mode.
              </span>
            </div>
          </div>
          <Button 
            size="sm" 
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={onRenew}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Renew Now
          </Button>
        </div>
      </div>
    );
  }
  
  // Expired (Read-Only Mode)
  if (is_expired || status === 'expired' || status === 'inactive') {
    return (
      <div className="bg-red-50 border-b border-red-300 px-4 py-3" data-testid="subscription-expired-banner">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-600" />
            <div>
              <span className="text-red-800 font-bold">Subscription Expired - READ ONLY MODE</span>
              <span className="text-red-700 ml-2">
                You can view data but cannot make any changes. Renew to restore full access.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="mailto:support@cloudcampus.com" className="text-red-600 hover:text-red-800 text-sm underline">
              Contact Support
            </a>
            <Button 
              size="sm" 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={onRenew}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Renew Subscription
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
}
